import React from "react"
import { TopActionBar } from "../../../../components"
import type { TableColumnsType } from "antd"
import { ResponsiveSegmentWrapper } from "components/custom/SegmentWrapper"
import { DashboardContainer } from "components"
import { DataEntity } from "../../extras/settings/permissions/typings"
import { useAuthStore } from "stores"
import { AppSearchInput } from "components/inputs/AppSearchInput"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import { companyService, COMPANY_ENDPOINTS } from "services/companyService"
import { AppTable } from "components/new-stuff/app-table"

interface IManagersPage {
  page: number
  limit: number
  nextPage: number | null
  previousPage: number | null
  hasNextPage: boolean
  hasPreviousPage: boolean
  totalDocs: number
  totalPages: number
  docs: DataEntity[]
}

const Managers: React.FC = () => {
  const { user } = useAuthStore()

  const {
    data,
    isLoading: isSetupDataLoading,
    isPreviousData,
    isSearching,
    search,
    handleSearchChange,
    tablePagination,
  } = useBackendTableQuery<IBaseRes<IManagersPage>>({
    queryKeyBase: [COMPANY_ENDPOINTS.SELECT_MANAGERS],
    queryFn: (params) => companyService.selectManagers(params),
    getTotal: (res) => res?.data?.totalDocs ?? 0,
    searchParamName: "searchParams",
    initialPageSize: 20,
    onSuccess() {
      mixPanelEvent(`people-managers-listing`)
    },
  })

  const TableColumns: TableColumnsType<DataEntity> = [
    {
      title: "Employee",
      dataIndex: "first_name",
      render: (_value, record) => (
        <div className="flex items-center gap-2 text-[#42526D] text-sm">
          <span className="shrink-0">
            <PersonAvatar
              className="w-8 h-8 rounded-full"
              src={record.avatar_url ?? undefined}
              alt=""
            />
          </span>
          <span>{record.first_name || "null"}</span>
          <span>{record.last_name || "null"}</span>
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "employee_title",
      render: (text: string) => (
        <span className="flex items-center gap-2 text-[#42526D] text-sm">
          {text}
        </span>
      ),
    },
  ]

  return (
    <>
      <TopActionBar>
        <div className="w-full">
          <AppSearchInput
            search={search}
            handleSearch={(value) => handleSearchChange(value)}
          />
        </div>
      </TopActionBar>

      <DashboardContainer>
        <ResponsiveSegmentWrapper
          title={`${user?.userOrganisation || "People"} Managers`}
          subtitle="People Managers oversee other employees at your Company. They manage
        their team’s requests, time tracking, and employee information."
        >
          <div className="w-full">
            <AppTable
              columns={TableColumns}
              dataSource={data?.data?.docs || []}
              rowKey="person_key"
              loading={isSetupDataLoading || isPreviousData || isSearching}
              pagination={tablePagination}
            />
          </div>
        </ResponsiveSegmentWrapper>
      </DashboardContainer>
    </>
  )
}

export default Managers
